import React, { useState, useEffect } from "react";
import { LGrid, LButton } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faEdit } from "@fortawesome/free-solid-svg-icons";
import styles from "../OutputDestinations.module.css";
import {GridCellProps, OutputDestination, OutputDestinationColumn} from "../types/outputDestinationsTypes";
import { DeleteConfirmation } from "../../../../../../TemplateManagement/TemplateManagementModals/SharedModals/DeleteConfirmationModal";
import { AddEditOutputDestinationModal } from "./AddEditOutputDestinationModal";
import { useAutoImportFormContext } from "../../../../Context/AutoImportFormContext";
import { getFileWriteBehaviorName } from "../utils/getFileWriteBehaviorName";
import { getOutputTypeName } from "../utils/getOutputTypeName";

function OutputDestinationsGrid() {
  const { error, updateFormData, formData } = useAutoImportFormContext();
  const [data, setData] = useState<OutputDestination[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedOutput, setSelectedOutput] = useState<OutputDestination | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<string | number | null>(null);
  
  useEffect(() => {
    setData(formData.outputDestinations?.length ? [...formData.outputDestinations] : []);
  }, [formData.outputDestinations]);

  const handleAddClick = () => {
    setSelectedOutput(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (item: OutputDestination) => {
    setSelectedOutput(item);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id: string | number) => {
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };

  //since all config data is going to sent as a batch to the API, the outputs are stored as state in the context
  const handleSaveOutputDestination = (newOutput: OutputDestination) => {
    const currentOutputs = formData.outputDestinations || [];
    const isEdit = currentOutputs.some(o => o.id === newOutput.id);
    const updated = isEdit
      ? currentOutputs.map(o => o.id === newOutput.id ? newOutput : o)
      : [...currentOutputs, newOutput];
  
    updateFormData({ outputDestinations: updated });
    setIsModalOpen(false);
  };

  const handleDeleteOutputDestination = (outputId: string | number) => {
    const currentOutputs = formData.outputDestinations || [];
    const updated = currentOutputs.filter((o) => o.id !== outputId);
    updateFormData({ outputDestinations: updated });
  };

  const columns: OutputDestinationColumn[] = [
    {
      field: "actions",
      title: "Actions",
      width: 100,
      cell: (props: GridCellProps) => (
        <td className={styles.actionButtons}>
          <button
            className={styles.editButton}
            onClick={() => handleEditClick(props.dataItem)}
            title="Edit"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            className={styles.deleteButton}
            onClick={() => handleDeleteClick(props.dataItem?.id)}
            title="Delete"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </td>
      ),
      filterable: false,
    },
    {
      field: "type",
      title: "Output Type",
      width: 125,
      cell: (props: GridCellProps) => (
        <td>{getOutputTypeName(props.dataItem.outputType?.value)}</td>
      ),
    },
    {
      field: "connection",
      title: "Connection",
      width: 225,
      cell: (props: GridCellProps) => (
        <td>
          {props.dataItem.connection.description}
        </td>
      ),
    },
    {
      field: "path",
      title: "Path",
      width: 200,
      cell: (props: GridCellProps) => (
        <td>{props.dataItem.connection?.rootPath || "-"}</td>
      ),
    },
    {
      field: "fileWriteBehavior",
      title: "File Write Behavior",
      width: 150,
      cell: (props: GridCellProps) => (
        <td>{getFileWriteBehaviorName(props.dataItem?.fileWriteBehavior.value)}</td>
      ),
    },
  ];

  return (
    <div className={styles.outputDestinationsContainer}>
      <div className={styles.header}>
        <h2>Output Destinations</h2>
        <LButton
          onClick={handleAddClick}
          themeColor="primary"
          className="green"
        >
          <FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />
          Add
        </LButton>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <LGrid
        key={data.length}
        data={data}
        columns={columns}
        sortable
        filterable
        resizable
        pageable
        style={{ height: "400px" }}
        className={styles.grid}
      />

      {isModalOpen && (
        <AddEditOutputDestinationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveOutputDestination}
          initialData={selectedOutput}
        />
      )}

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          if (itemToDelete) {
            handleDeleteOutputDestination(itemToDelete);
            setIsDeleteModalOpen(false);
          }
        }}
        title="Confirm Delete"
        message="Are you sure you want to delete this output destination? This action cannot be undone."
      />
    </div>
  );
}

export { OutputDestinationsGrid };